import _ from 'lodash'
import { BaseDataItem, InnerElementsDataItem, pointers } from '@wix/thunderbolt-becky-types'
import type { Ref } from '@wix/thunderbolt-types'
import { createConsumptionDataNode } from './utils/dataNodeUtils'
import type { DataAndTranslationsWithOverridesRefs } from '../../types'
import { removeHash } from '@wix/thunderbolt-catharsis'
import { BUILDER_COMP_TO_NICKNAME_MAP_ASPECT, RESOLVED_TRANSLATIONS_ASPECT, SITE_NAMESPACE } from '../../../constants'
import { getDataItemRefsWithOverrides } from '../../dataConsumption'
import type { DependenciesData } from './types'
import { createRef } from '@wix/materializer/dist'
import { getDataPathForInnerElement, getSplitCompIdsFromInflatedId } from '../../../byRefUtils'

type Dependencies = {
	translationDataItemWithOverrides: Array<Ref<any>>
	dataItemWithOverrides: Array<any>
	refCompInnerElementsData: Ref<InnerElementsDataItem> | null
}

const {
	data: {
		DATA_MAPS: { INNER_ELEMENTS },
	},
} = pointers

export const DocumentData = createConsumptionDataNode<
	DependenciesData,
	Dependencies,
	DataAndTranslationsWithOverridesRefs & { refCompInnerElementsData: Ref<InnerElementsDataItem> | null }
>({
	getDependencies: ({
		queryName,
		templateComponent,
		getOverrideDataRefs,
		createRefInTemplateNamespace,
		getOverrideTranslationDataRefs,
		itemSuffix,
		refCompInnerElementsQuery,
	}) => ({
		dataItemWithOverrides: getDataItemRefsWithOverrides({
			templateComponent,
			queryName,
			createRefInTemplateNamespace,
			getOverrideDataRefs,
			itemSuffix,
		}),
		translationDataItemWithOverrides: [
			...getOverrideTranslationDataRefs(templateComponent.id, itemSuffix),
			createRefInTemplateNamespace<BaseDataItem>(
				RESOLVED_TRANSLATIONS_ASPECT,
				`${removeHash(templateComponent[queryName]!)}${itemSuffix}`
			),
		],
		refCompInnerElementsData: refCompInnerElementsQuery
			? createRef([SITE_NAMESPACE, INNER_ELEMENTS, refCompInnerElementsQuery])
			: null,
	}),
	toViewItem: ({
		id,
		value: { dataItemWithOverrides, translationDataItemWithOverrides, refCompInnerElementsData },
		rawModel,
	}) => {
		const ancestorChain = getSplitCompIdsFromInflatedId(id.replace(/-dataQuery/, ''))
		const compIdToNicknameMap =
			_.get(rawModel, [SITE_NAMESPACE, BUILDER_COMP_TO_NICKNAME_MAP_ASPECT, ancestorChain[0]]) || {}

		const dataPathForInnerElement = getDataPathForInnerElement({
			ancestorChain: ancestorChain.slice(2),
			compIdToNicknameMap,
			firstInnerWidgetId: ancestorChain[1],
			extraPath: ['dataQuery', 'data'],
		})

		const innerElementOverride = _.get(refCompInnerElementsData, dataPathForInnerElement)

		for (const index in dataItemWithOverrides) {
			const translatedDataItem = dataItemWithOverrides[index] // translated in ModelUpdateTransformer
			const dataItemFromTranslationsMap = translationDataItemWithOverrides![index]

			if (!translatedDataItem && !dataItemFromTranslationsMap) {
				continue
			}

			return { ...(translatedDataItem || dataItemFromTranslationsMap), ...innerElementOverride, id }
		}
	},
})
